import { inject, Injectable } from '@angular/core';
import { Auth, authState } from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { orderBy, where } from '@angular/fire/firestore';
import {
  AvailabilityPoll,
  AvailabilityResponse,
  CreateAvailabilityPollPayload,
} from '@models';
import { from, Observable, shareReplay, switchMap, take } from 'rxjs';
import { FirestoreService } from './firestore.service';
import { TenantContextService } from './tenant-context.service';

@Injectable({
  providedIn: 'root',
})
export class AvailabilityService {
  private readonly firestore = inject(FirestoreService);
  private readonly tenantContext = inject(TenantContextService);
  private readonly auth = inject(Auth);
  private readonly functions = inject(Functions);

  private get col() {
    return this.tenantContext.collections.AVAILABILITY_POLLS;
  }

  byTeam$(teamId: string): Observable<AvailabilityPoll[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<AvailabilityPoll>(
          this.col,
          where('teamId', '==', teamId),
          orderBy('createdAt', 'desc'),
        ),
      ),
      shareReplay(1),
    );
  }

  byMatch$(matchId: string): Observable<AvailabilityPoll[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<AvailabilityPoll>(
          this.col,
          where('matchId', '==', matchId),
        ),
      ),
      shareReplay(1),
    );
  }

  getResponses$(pollId: string): Observable<AvailabilityResponse[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<AvailabilityResponse>(
          `${this.col}/${pollId}/responses`,
        ),
      ),
      shareReplay(1),
    );
  }

  async createPoll(payload: CreateAvailabilityPollPayload): Promise<string> {
    const pollId = await this.firestore.create<AvailabilityPoll>(this.col, {
      ...payload,
      status: 'OPEN' as const,
    });
    try {
      const callable = httpsCallable(
        this.functions,
        'adminSendPushNotification',
      );
      await callable({
        teamId: payload.teamId,
        type: 'availability_poll',
        pollId,
      });
    } catch {
      // Non-blocking — poll is created even if notification fails
    }
    return pollId;
  }

  async closePoll(pollId: string): Promise<void> {
    return this.firestore.update<AvailabilityPoll>(this.col, {
      id: pollId,
      status: 'CLOSED',
    });
  }

  async respondToPoll(
    pollId: string,
    available: boolean,
    reason?: string,
  ): Promise<void> {
    const user = await new Promise<{ uid: string } | null>((resolve) => {
      authState(this.auth).pipe(take(1)).subscribe(resolve);
    });
    if (!user) throw new Error('Unauthenticated');
    const responsePath = `${this.col}/${pollId}/responses`;
    return this.firestore.createOrUpdate<AvailabilityResponse>(
      responsePath,
      user.uid,
      {
        id: user.uid,
        pollId,
        userId: user.uid,
        available,
        reason,
        respondedAt: new Date(),
      },
    );
  }
}
